import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { COMPANY, COMPANY_INFO, AI_SATEI_URL } from '@/constants/company';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

const siteUrl = 'https://clearcorp.info';

const companyJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/company#webpage`,
      url: `${siteUrl}/company`,
      name: 'クリアコーポレーション 会社概要｜名古屋西区の不動産会社',
      description:
        '株式会社クリアコーポレーション（クリアコーポレーション）の会社概要。愛知県知事（1）第25984号の宅建免許取得。名古屋市西区江向町五丁目15番地1。代表取締役 溝口富久。名古屋16区の不動産売却・買取・査定に対応。',
      inLanguage: 'ja',
    },
    {
      '@type': 'RealEstateAgent',
      '@id': `${siteUrl}/#organization`,
      name: '株式会社クリアコーポレーション',
      alternateName: ['クリアコーポレーション', 'Clear Corporation'],
      url: siteUrl,
      telephone: '052-325-5812',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '江向町五丁目15番地1',
        addressLocality: '名古屋市西区',
        addressRegion: '愛知県',
        postalCode: '451-0013',
        addressCountry: 'JP',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 35.1856,
        longitude: 136.8786,
      },
      founder: {
        '@type': 'Person',
        name: '溝口富久',
        jobTitle: '代表取締役',
      },
      numberOfEmployees: {
        '@type': 'QuantitativeValue',
        value: 5,
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          opens: '09:00',
          closes: '17:00',
        },
      ],
      areaServed: '名古屋市全16区・全国',
      description:
        '名古屋市西区に拠点を置く不動産会社クリアコーポレーション。名古屋16区の不動産売却・買取・査定に対応。AI査定約60秒・秘密厳守・愛知県知事免許取得の信頼ある宅建業者。',
      priceRange: '無料査定',
      hasMap: `https://maps.google.com/?q=${encodeURIComponent('愛知県名古屋市西区江向町五丁目15番地1')}`,
    },
  ],
};

const stats = [
  { value: '16区', label: '名古屋全区対応', icon: 'ri-map-pin-line' },
  { value: '60秒', label: 'AI査定スピード', icon: 'ri-time-line' },
  { value: '全国', label: '相談対応エリア', icon: 'ri-earth-line' },
  { value: '秘密厳守', label: 'プライバシー保護', icon: 'ri-lock-line' },
];

const strengths = [
  {
    icon: 'ri-user-heart-line',
    title: 'お客様第一主義',
    desc: 'お客様の状況や想いを最優先に考え、本当に必要なアドバイスとサポートを提供します。押し売りは絶対に行いません。一人ひとりに寄り添った丁寧な対応が私たちのモットーです。',
  },
  {
    icon: 'ri-award-line',
    title: 'プロフェッショナル精神',
    desc: '宅建士をはじめとした専門資格を持つスタッフが、法的・税務的な観点からも適切なアドバイスを行います。愛知県知事免許（第25984号）取得の信頼ある宅建業者です。',
  },
  {
    icon: 'ri-building-line',
    title: '名古屋16区に強い地域密着',
    desc: '名古屋市内16区すべてをカバーし、各エリアの相場・特性を熟知しています。地元密着のネットワークで、迅速かつ適正な査定・売却サポートを実現します。',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'プライバシー完全厳守',
    desc: 'ご相談内容や個人情報は完全に秘密厳守。ご近所に知られず、ご家族にも内密なまま進めることができます。秘密裏に売却を進めたいお客様も安心してご相談ください。',
  },
  {
    icon: 'ri-speed-line',
    title: 'AI査定でスピード対応',
    desc: '約60秒で物件価格の目安がわかるAI査定を導入。まず価格感を掴んでから相談したいお客様にも対応。正式な訪問査定も素早くスケジュール調整いたします。',
  },
  {
    icon: 'ri-global-line',
    title: '全国どこからでも相談OK',
    desc: '名古屋16区に強みを持ちつつ、全国からのご相談にも対応しております。オーナーが遠方に住んでいる場合や、遠方から名古屋の物件を売りたい方もお気軽にどうぞ。',
  },
];

const history = [
  { year: '2020年', event: '株式会社クリアコーポレーション 設立', detail: '愛知県名古屋市西区にて不動産売買仲介・買取業として創業。' },
  { year: '2021年', event: '愛知県知事免許 取得', detail: '宅地建物取引業 愛知県知事（1）第25984号 取得。正式に宅建業をスタート。' },
  { year: '2022年', event: 'AI査定サービス導入', detail: '約60秒で物件価格の目安がわかるAI査定ツールを導入。オンライン相談体制を整備。' },
  { year: '2023年', event: '名古屋16区 全エリア対応開始', detail: '名古屋市内全16区への地域密着型サービスを本格展開。相談実績を積み重ねる。' },
  { year: '2025年', event: '公式ウェブサイトリニューアル', detail: 'クリアコーポレーション公式サイトを全面刷新。AI査定・物件情報・お客様の声を掲載。' },
];

export default function CompanyPage() {
  const mapRef = useRef<HTMLIFrameElement>(null);

  return (
    <>
      <title>クリアコーポレーション 会社概要｜名古屋西区の不動産会社</title>
      <meta
        name="description"
        content="株式会社クリアコーポレーション（クリアコーポレーション）の会社概要ページ。名古屋市西区の不動産会社。宅建免許：愛知県知事（1）第25984号。代表取締役 溝口富久。名古屋16区の不動産売却・買取・査定に対応。"
      />
      <meta
        name="keywords"
        content="クリアコーポレーション, 株式会社クリアコーポレーション, 名古屋西区 不動産会社, 名古屋 不動産売却, 愛知県 宅建業者"
      />
      <meta property="og:title" content="クリアコーポレーション 会社概要｜名古屋西区の不動産会社" />
      <meta
        property="og:description"
        content="クリアコーポレーション（株式会社クリアコーポレーション）の会社概要。名古屋16区の不動産売却・買取・査定に対応。愛知県知事免許取得。"
      />
      <meta property="og:type" content="website" />
      <link rel="canonical" href="https://clearcorp.info/company" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(companyJsonLd) }}
      />

      <Header />
      <main className="min-h-screen bg-white">
        {/* Hero */}
        <section
          className="relative py-24 md:py-36 overflow-hidden"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/a8fb1ae7d49ae3917ba93a0fb6073519.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-[#1A2B4A]/70" />
          <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center">
            <p className="text-emerald-400 text-sm font-semibold tracking-widest uppercase mb-3">
              Company Profile
            </p>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-5 leading-tight">
              クリアコーポレーション<br />
              <span className="text-xl md:text-3xl font-normal text-white/80">株式会社クリアコーポレーション</span>
            </h1>
            <p className="text-white/75 text-base md:text-lg leading-relaxed">
              名古屋市西区に根差し、地域の不動産に精通した<br className="hidden md:block" />
              プロフェッショナル集団です。
            </p>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#1A2B4A] py-8">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((s) => (
                <div key={s.label} className="text-center">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                    <i className={`${s.icon} text-2xl text-emerald-400`} />
                  </div>
                  <p className="text-white text-2xl font-bold">{s.value}</p>
                  <p className="text-white/60 text-xs mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Company Overview Table */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-emerald-600 text-sm font-semibold tracking-widest uppercase mb-2">Overview</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A2B4A]">基本情報</h2>
            </div>
            <div className="border border-gray-200 rounded-2xl overflow-hidden">
              {[
                { label: '会社名', value: '株式会社クリアコーポレーション（クリアコーポレーション）' },
                { label: '英語表記', value: 'Clear Corporation Co., Ltd.' },
                { label: '代表者', value: COMPANY_INFO.representative },
                { label: '所在地', value: `${COMPANY.zip} ${COMPANY.address}` },
                { label: '電話番号', value: COMPANY.tel, isPhone: true },
                { label: '営業時間', value: `${COMPANY.hours}（年中無休）` },
                { label: '宅建免許番号', value: COMPANY_INFO.licenseNumber },
                { label: '免許有効期間', value: COMPANY_INFO.licensePeriod },
                { label: '対応エリア', value: '名古屋市全16区・愛知県全域・全国（遠方相談可）' },
                { label: '主要業務', value: '不動産売却仲介・不動産買取・不動産査定・資産活用相談' },
              ].map((row, i) => (
                <div
                  key={row.label}
                  className={`grid md:grid-cols-3 ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                >
                  <dt className="px-6 py-4 text-sm font-semibold text-[#1A2B4A] border-b border-gray-100 md:border-b-0 md:border-r md:border-gray-200 flex items-center">
                    {row.label}
                  </dt>
                  <dd className="md:col-span-2 px-6 py-4 text-sm text-gray-700 border-b border-gray-100 flex items-center">
                    {row.isPhone ? (
                      <a href={COMPANY.telHref} className="hover:text-emerald-600 transition-colors font-medium">
                        {row.value}
                      </a>
                    ) : (
                      row.value
                    )}
                  </dd>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Representative Message */}
        <section className="py-20 md:py-28 bg-[#F8F9FB]">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-emerald-600 text-sm font-semibold tracking-widest uppercase mb-2">Message</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A2B4A]">代表挨拶</h2>
            </div>
            <div className="bg-white rounded-2xl overflow-hidden">
              <div className="grid md:grid-cols-1">
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <h3 className="text-lg font-bold text-[#1A2B4A] mb-1">代表取締役</h3>
                  <p className="text-2xl font-bold text-[#1A2B4A] mb-6">溝口 富久</p>
                  <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
                    <p>
                      弊社クリアコーポレーションは、名古屋市西区を拠点に不動産の売却・買取サポートを行っております。
                      「不動産の悩みを、もっとシンプルに、もっと安心して解決したい」— その思いから創業し、
                      お客様一人ひとりと真摯に向き合ってまいりました。
                    </p>
                    <p>
                      不動産の売却は、多くの方にとって人生で数回しか経験しない大きな決断です。
                      だからこそ、押し売りはせず、お客様の状況や想いをしっかりとお聞きし、
                      本当に必要なアドバイスを提供することが私たちの使命だと考えています。
                    </p>
                    <p>
                      名古屋16区の地元密着型ネットワークと豊富な実績を活かし、
                      これからも地域の皆様の「不動産の困った」を解決するパートナーであり続けます。
                      どんな小さなご相談でも、お気軽にクリアコーポレーションへお問い合わせください。
                    </p>
                  </div>
                  <div className="mt-8 pt-6 border-t border-gray-100">
                    <p className="text-xs text-gray-400">宅地建物取引士 / 愛知県知事免許（1）第25984号</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Strengths */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-emerald-600 text-sm font-semibold tracking-widest uppercase mb-2">Our Strengths</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A2B4A]">クリアコーポレーションの強み</h2>
              <p className="text-gray-500 text-sm mt-3">選ばれる6つの理由</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {strengths.map((s, i) => (
                <div key={i} className="bg-[#F8F9FB] rounded-xl p-7 hover:bg-[#EEF2F8] transition-colors duration-200">
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl mb-5">
                    <i className={`${s.icon} text-2xl text-emerald-600`} />
                  </div>
                  <h3 className="text-base font-bold text-[#1A2B4A] mb-3">{s.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* History */}
        <section className="py-20 md:py-28 bg-[#F8F9FB]">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-emerald-600 text-sm font-semibold tracking-widest uppercase mb-2">History</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A2B4A]">会社沿革</h2>
            </div>
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />
              <div className="space-y-8">
                {history.map((h, i) => (
                  <div key={i} className="relative flex items-start gap-6 pl-12">
                    {/* Year badge */}
                    <div className="absolute left-0 top-1 z-10 w-8 h-8 flex items-center justify-center bg-[#1A2B4A] rounded-full flex-shrink-0">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full" />
                    </div>
                    {/* Content */}
                    <div className="flex-1 pb-2">
                      <p className="text-emerald-600 text-xs font-bold mb-1">{h.year}</p>
                      <p className="text-[#1A2B4A] font-bold text-sm mb-1">{h.event}</p>
                      <p className="text-gray-500 text-xs leading-relaxed">{h.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Access / Map */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-emerald-600 text-sm font-semibold tracking-widest uppercase mb-2">Access</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A2B4A]">アクセス</h2>
            </div>
            <div className="grid md:grid-cols-5 gap-8 items-start">
              <div className="md:col-span-2 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#F0F4FA] rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-[#1A2B4A] text-lg" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">所在地</p>
                    <p className="text-sm text-gray-800 font-medium">{COMPANY.zip}</p>
                    <p className="text-sm text-gray-800">{COMPANY.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#F0F4FA] rounded-lg flex-shrink-0">
                    <i className="ri-train-line text-[#1A2B4A] text-lg" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">最寄り駅</p>
                    <p className="text-sm text-gray-800">名古屋市営地下鉄 鶴舞線</p>
                    <p className="text-sm font-medium text-[#1A2B4A]">「庄内通駅」より徒歩7分</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#F0F4FA] rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-[#1A2B4A] text-lg" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">電話番号</p>
                    <a href={COMPANY.telHref} className="text-sm font-bold text-[#1A2B4A] hover:text-emerald-600 transition-colors">
                      {COMPANY.tel}
                    </a>
                    <p className="text-xs text-gray-400 mt-0.5">受付時間 {COMPANY.hours}</p>
                  </div>
                </div>
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent('愛知県名古屋市西区江向町五丁目15番地1')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer mt-2"
                >
                  <i className="ri-external-link-line" />
                  Googleマップで開く
                </a>
              </div>
              <div className="md:col-span-3 rounded-2xl overflow-hidden" style={{ height: '360px' }}>
                <iframe
                  ref={mapRef}
                  src="https://maps.google.com/maps?q=%E6%84%9B%E7%9F%A5%E7%9C%8C%E5%90%8D%E5%8F%A4%E5%B1%8B%E5%B8%82%E8%A5%BF%E5%8C%BA%E6%B1%9F%E5%90%91%E7%94%BA%E4%BA%94%E4%B8%81%E7%9B%AE15%E7%95%AA%E5%9C%B01&t=&z=16&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="株式会社クリアコーポレーション アクセスマップ"
                />
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}